import React from 'react';
import ComputerOutlined from '@mui/icons-material/ComputerOutlined';
import ExtensionOutlined from '@mui/icons-material/ExtensionOutlined';

import type { NavItem, ShellData } from './ShellData';
import dooglieLogo from '../images/login/DOL_Standalone.svg';
import '../styles/ui.css';
import '../styles/demo.css';

/**
 * The authed-page layout as a pure view: the cobalt sidebar (pages, the
 * desktop / extension install items, admin sections, staff demo button, sign
 * out), the top banners, and a scrollable main area for the page.
 *
 * It reads nothing but its props, so the onboarding tour can draw it from a
 * script. `AppShell` is the container that fills `ShellData` for real.
 */
const ShellView: React.FC<ShellData & { children?: React.ReactNode }> = ({
  items,
  active,
  extension,
  desktop,
  onboardingDot,
  onNavigate,
  onDownloadExtension,
  onDownloadDesktop,
  demo,
  actingAs,
  extensionOff,
  onSignOut,
  children,
}) => {
  const navButton = (item: NavItem) => (
    <button
      key={item.id}
      type="button"
      data-tour={item.tour}
      className={`dg-item ${active === item.id ? 'active' : ''}`}
      onClick={() => onNavigate(item.route)}
    >
      <item.icon className="dg-item-icon" aria-hidden />
      {item.label}
      {item.id === 'onboarding' && onboardingDot && (
        <span className="dg-nav-dot" role="status" aria-label="Not finished yet" />
      )}
    </button>
  );
  const main = items.filter((i) => i.section === 'main');
  const admin = items.filter((i) => i.section === 'admin');

  return (
    <div className="dg-shell-frame">
      {actingAs && (
        <div className="dg-acting-banner" role="alert">
          <span>
            Acting as <b>{actingAs.name}</b>
            {actingAs.orgName ? ` (${actingAs.orgName})` : ''}
          </span>
          <button type="button" className="dg-btn dg-btn-inverse dg-btn-sm" onClick={actingAs.onStop}>
            Stop
          </button>
          {actingAs.stopError && <span className="dg-acting-error">{actingAs.stopError}</span>}
        </div>
      )}
      {extensionOff && (
        <div className="dg-ext-off-banner" role="status">
          <span>
            A Chrome update switched Dooglie off — open <code>chrome://extensions</code> and
            turn Dooglie back on.
          </span>
          <button type="button" className="dg-btn dg-btn-ghost dg-btn-sm" onClick={extensionOff.onDismiss}>
            Dismiss
          </button>
        </div>
      )}
      <div className="dg-shell">
        <nav className="dg-sidebar">
          <div className="dg-logo" onClick={() => onNavigate('/home')}>
            <img src={dooglieLogo} alt="Dooglie" />
            <span>Dooglie</span>
          </div>

          {main.map(navButton)}

          <div className="dg-lbl">Apps</div>
          <button
            type="button"
            data-tour="shell.nav.extension"
            data-status={extension}
            className={`dg-item ${extension === 'installed' ? 'done' : ''}`}
            disabled={extension !== 'missing'}
            onClick={() => extension === 'missing' && onDownloadExtension()}
          >
            <ExtensionOutlined className="dg-item-icon" aria-hidden />
            {extension === 'checking'
              ? 'Checking extension…'
              : extension === 'installed'
                ? 'Extension added'
                : 'Download extension'}
          </button>
          <button
            type="button"
            data-tour="shell.nav.desktop"
            data-status={desktop}
            className={`dg-item ${desktop === 'installed' ? 'done' : ''}`}
            onClick={onDownloadDesktop}
          >
            <ComputerOutlined className="dg-item-icon" aria-hidden />
            {desktop === 'installed' ? 'Desktop app' : 'Download desktop app'}
          </button>

          {admin.length > 0 && (
            <>
              <div className="dg-lbl">Admin</div>
              {admin.map(navButton)}
            </>
          )}

          <div className="dg-sidebar-bottom">
            {demo && (
              <button
                type="button"
                className={`dg-demo-btn ${demo.status !== 'idle' ? 'live' : ''}`}
                onClick={demo.onOpen}
              >
                {demo.status === 'ready' ? (
                  <>
                    <span className="dg-demo-live-dot" />
                    Demo ready
                  </>
                ) : demo.status === 'preparing' ? (
                  <>
                    <span className="dg-demo-live-dot" />
                    Preparing a demo…
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M8 5v14l11-7Z" />
                    </svg>
                    Prepare a demo
                  </>
                )}
              </button>
            )}
            <button type="button" className="dg-item" onClick={onSignOut}>
              Sign out
            </button>
          </div>
        </nav>
        <main className="dg-main">{children}</main>
      </div>
    </div>
  );
};

export default ShellView;
